// Section 24.7: validateDecision returns structured problems the UI shows inline; step() also
// validates and throws, so a UI bug cannot drive the sim into a bad state.
import { config, type Config } from '../config';
import { registryFor } from '../content';
import { buildingCount, isChild, skillOf } from '../core/queries';
import type {
  BuildingTypeId,
  Decision,
  GameState,
  JobId,
  PersonId,
  PolicyId,
  PromptAnswer,
  ValidationProblem,
} from '../core/types';
import { buildingLimit } from '../phases/shared/capacity';
import { workBlock } from '../phases/shared/eligibility';
import { readContext } from './context';

const BLOCK_MESSAGES: Record<NonNullable<ReturnType<typeof workBlock>>, string> = {
  'too-young': 'is too young to work',
  'child-labour': 'is a child and the child-labour policy does not allow it',
  'elder-heavy': 'is an elder and cannot do heavy work',
  'needs-building': 'cannot do this job without its building',
  'too-weak': 'is not strong enough for this job',
  trait: 'will not do this job',
  'job-rule': 'is not eligible for this job',
  schooling: 'must attend school under the compulsory schooling policy',
};

function answerMatches(kind: string, answer: PromptAnswer): boolean {
  return answer.kind === kind;
}

/** Section 9 limits (none, fixed, 1 per N people); the same rule production applies. */
export function buildLimit(state: GameState, type: BuildingTypeId, cfg: Config = config): number {
  return buildingLimit(registryFor(cfg), state, type);
}

export function validateDecision(
  state: GameState,
  decision: Decision,
  cfg: Config = config,
): ValidationProblem[] {
  const problems: ValidationProblem[] = [];
  const ctx = readContext(state, cfg);
  const registry = ctx.registry;
  const add = (path: string, code: ValidationProblem['code'], message: string): void => {
    problems.push({ path, code, message });
  };
  if (state.outcome !== null && !state.sandbox) add('', 'game_over', 'The game has ended.');
  if (state.phaseCursor !== null)
    add('', 'paused', 'The season is paused on a prompt; answer it first.');
  if (decision.turn !== state.turn)
    add(
      'turn',
      'stale_turn',
      `Decision is for turn ${decision.turn}, the game is at ${state.turn}.`,
    );

  for (const [id, job] of Object.entries(decision.assignments)) {
    const p = state.people[id as keyof typeof state.people];
    if (p === undefined) add(`assignments.${id}`, 'unknown_person', `No person ${id}.`);
    else if (!(job in registry.jobs)) add(`assignments.${id}`, 'unknown_job', `No job ${job}.`);
    else {
      const block = workBlock(ctx, p, job);
      if (block !== null)
        add(`assignments.${id}`, 'not_eligible', `${p.name} ${BLOCK_MESSAGES[block]}.`);
    }
  }

  for (const [id, value] of Object.entries(decision.policies)) {
    if (!(id in registry.policies)) {
      add(`policies.${id}`, 'unknown_policy', `No policy ${id}.`);
      continue;
    }
    const def = registry.policies[id as PolicyId] as unknown as {
      options: readonly unknown[] | null;
      validate: (c: typeof ctx, v: unknown) => ValidationProblem | null;
    };
    if (
      def.options !== null &&
      !def.options.some((o) => JSON.stringify(o) === JSON.stringify(value))
    ) {
      add(
        `policies.${id}`,
        'invalid_option',
        `${JSON.stringify(value)} is not an option for ${id}.`,
      );
      continue;
    }
    const problem = def.validate(ctx, value);
    if (problem !== null) problems.push(problem);
  }

  for (const [resource, overrides] of Object.entries(decision.rations)) {
    for (const [id, share] of Object.entries(overrides.people)) {
      if (state.people[id as keyof typeof state.people] === undefined)
        add(`rations.${resource}.${id}`, 'unknown_person', `No person ${id}.`);
      if (!(share >= 0 && share <= 1))
        add(
          `rations.${resource}.${id}`,
          'out_of_range',
          'Ration pins are fractions of need, 0 to 1.',
        );
    }
  }

  // Section 9 (stream M3a): a new building counts against its limit with the standing ones, the
  // queued orders not cancelled in this decision and those queued earlier in it. A repair needs
  // a standing building of that type with no repair already queued.
  const cancelled = new Set<string>(
    decision.builds.flatMap((c) => (c.kind === 'cancel' ? [c.orderId] : [])),
  );
  const repairing = new Set<string>(
    state.buildQueue.flatMap((o) =>
      o.repairOf !== null && !cancelled.has(o.id) ? [o.repairOf] : [],
    ),
  );
  const queued = new Map<string, number>();
  decision.builds.forEach((cmd, i) => {
    const path = `builds.${i}`;
    if (cmd.kind === 'queue') {
      if (!(cmd.buildingType in registry.buildings)) {
        add(path, 'unknown_building', `No building ${cmd.buildingType}.`);
        return;
      }
      const label = registry.buildings[cmd.buildingType].label.toLowerCase();
      if (cmd.repairOf !== null) {
        if (state.buildings[cmd.repairOf]?.type !== cmd.buildingType)
          add(path, 'unknown_building', `No ${label} ${cmd.repairOf} to repair.`);
        else if (repairing.has(cmd.repairOf))
          add(path, 'limit_reached', `The ${label} is already queued for repair.`);
        repairing.add(cmd.repairOf);
        return;
      }
      const planned = state.buildQueue.filter(
        (o) => o.buildingType === cmd.buildingType && o.repairOf === null && !cancelled.has(o.id),
      ).length;
      const n = (queued.get(cmd.buildingType) ?? 0) + 1;
      queued.set(cmd.buildingType, n);
      if (
        buildingCount(state, cmd.buildingType) + planned + n >
        buildLimit(state, cmd.buildingType, cfg)
      ) {
        add(
          path,
          'limit_reached',
          `The ${registry.buildings[cmd.buildingType].label.toLowerCase()} limit is reached.`,
        );
      }
    } else if (!state.buildQueue.some((o) => o.id === cmd.orderId)) {
      add(path, 'unknown_order', `No build order ${cmd.orderId}.`);
    }
  });

  validateApprenticeships(state, decision, cfg, add);

  for (const prompt of state.openPrompts) {
    const answer = decision.eventChoices[prompt.id];
    if (answer === undefined)
      add(`eventChoices.${prompt.id}`, 'missing_answer', 'An open prompt needs an answer.');
    else if (!answerMatches(prompt.kind, answer))
      add(`eventChoices.${prompt.id}`, 'invalid_answer', 'Answer kind does not match the prompt.');
    else if (
      prompt.kind === 'event' &&
      answer.kind === 'event' &&
      !prompt.choices.some((c) => c.id === answer.choiceId)
    ) {
      add(`eventChoices.${prompt.id}`, 'invalid_answer', `No choice ${answer.choiceId}.`);
    } else if (
      // Section 13 (stream M4a): a case may be answered only with a response open to it.
      prompt.kind === 'justice' &&
      answer.kind === 'justice' &&
      !prompt.options.includes(answer.response)
    ) {
      add(
        `eventChoices.${prompt.id}`,
        'invalid_answer',
        `${answer.response} is not available for this case.`,
      );
    }
  }
  for (const id of Object.keys(decision.eventChoices)) {
    if (!state.openPrompts.some((p) => p.id === id))
      add(`eventChoices.${id}`, 'unknown_prompt', `No open prompt ${id}.`);
  }
  for (const [id, name] of Object.entries(decision.renames)) {
    if (state.people[id as keyof typeof state.people] === undefined)
      add(`renames.${id}`, 'unknown_person', `No person ${id}.`);
    if (name.trim().length === 0 || name.length > 40)
      add(`renames.${id}`, 'invalid_name', 'Names are 1 to 40 characters.');
  }
  return problems;
}

/**
 * Section 11 apprenticeship (Stream D): the apprentice is an adult working the master's job, the
 * master has skill 4+ in that job, and a master takes at most two apprentices.
 */
function validateApprenticeships(
  state: GameState,
  decision: Decision,
  cfg: Config,
  add: (path: string, code: ValidationProblem['code'], message: string) => void,
): void {
  const jobAfter = (id: PersonId): JobId | undefined =>
    decision.assignments[id] ?? state.people[id]?.job;
  const masters = new Map<PersonId, number>();
  for (const p of Object.values(state.people)) {
    const master = p.id in decision.apprenticeships ? decision.apprenticeships[p.id] : p.masterId;
    if (master !== null && master !== undefined && state.people[master] !== undefined)
      masters.set(master, (masters.get(master) ?? 0) + 1);
  }
  for (const [id, masterId] of Object.entries(decision.apprenticeships)) {
    const path = `apprenticeships.${id}`;
    const p = state.people[id as PersonId];
    if (p === undefined) {
      add(path, 'unknown_person', `No person ${id}.`);
      continue;
    }
    if (masterId === null) continue;
    const m = state.people[masterId];
    if (m === undefined) {
      add(path, 'unknown_person', `No person ${masterId}.`);
      continue;
    }
    const job = jobAfter(m.id);
    if (masterId === p.id || isChild(cfg, p))
      add(path, 'not_eligible', `${p.name} cannot be apprenticed to ${m.name}.`);
    else if (job === undefined || job === 'rest' || skillOf(m, job) < cfg.skills.masterMinSkill)
      add(
        path,
        'not_eligible',
        `${m.name} is not a master (skill ${cfg.skills.masterMinSkill}+) in their job.`,
      );
    else if (jobAfter(p.id) !== job)
      add(path, 'not_eligible', `${p.name} must work alongside ${m.name} to learn from them.`);
    else if ((masters.get(m.id) ?? 0) > cfg.skills.maxApprentices)
      add(path, 'not_eligible', `${m.name} already has ${cfg.skills.maxApprentices} apprentices.`);
  }
}
